import React, { useEffect, useState, useMemo } from 'react';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from './ChartLayout';
import { SectorBasedGHGInventory } from './SectorBasedGHGInventory';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import { fetchGHGInventoryData, GHGInventoryResult } from './GHGInventorySupabase';
import { useLocation } from '../../contexts/LocationContext';
import { DataReviewBanner } from './DataReviewBanner';
import { isElectricityDataUnderReview, getLocationNameUnderReview, isElectricityDataUnderReviewByName, getGeofipsFromLocationName } from '../../utils/dataReviewLocations';

type ChartType = 'stacked-area' | 'line' | 'pie';

interface SectorBasedGHGInventoryWithMetricsProps {
  location: string;
}

export const SectorBasedGHGInventoryWithMetrics: React.FC<SectorBasedGHGInventoryWithMetricsProps> = ({ location }) => {
  const { coreDataLoading, selectedGeofips } = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartType, setChartType] = useState<ChartType>('stacked-area');
  const [activeView, setActiveView] = useState<string>('overview');
  const [ghgData, setGhgData] = useState<GHGInventoryResult | null>(null);

  // Load real GHG inventory data
  // Wait for core data (VMT and energy) to finish loading when location changes
  // LocationContext already clears and reloads VMT/energy caches when location changes
  useEffect(() => {
    const loadData = async () => {
      if (!location) return;
      
      // Wait for core data loading to complete (VMT and energy data reload)
      // LocationContext handles clearing and reloading VMT/energy data when location changes
      if (coreDataLoading) {
        console.log('GHG Inventory: Waiting for VMT and energy data to reload for', location);
        setLoading(true); // Show loading state while waiting
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch GHG inventory data - it will use the freshly loaded VMT and energy data
        // that LocationContext just reloaded
        const result = await fetchGHGInventoryData(location);
        setGhgData(result);
      } catch (err) {
        console.error('Error loading GHG inventory data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load GHG inventory data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [location, coreDataLoading]);

  // Calculate comprehensive metrics from chart data
  // Must be before conditional returns to satisfy React Hooks rules
  const metrics = useMemo(() => {
    if (!ghgData?.chartData) return null;

    const years = ghgData.chartData.labels || [];
    const datasets = ghgData.chartData.datasets || [];

    // Helper to get emissions for a category and year
    const getEmissions = (category: string, year: number): number => {
      const dataset = datasets.find((d: any) => d.label === category);
      if (!dataset) return 0;
      const yearIndex = years.indexOf(year);
      return yearIndex >= 0 ? (dataset.data[yearIndex] || 0) : 0;
    };

    // Get latest year (typically 2023) and key years
    const latestYear = years.length > 0 ? years[years.length - 1] : 2023;
    const year2005 = years.includes(2005) ? 2005 : null;
    const year2020 = years.includes(2020) ? 2020 : null;

    // Calculate totals for 2005 (baseline year)
    let total2005 = 0;
    if (year2005) {
      const transportation2005 = getEmissions('Light Duty Vehicles', 2005) + getEmissions('Heavy Duty Vehicles', 2005);
      const electricity2005 = getEmissions('Electricity Residential', 2005) + getEmissions('Electricity Commercial', 2005);
      const naturalGas2005 = getEmissions('Natural Gas Residential', 2005) + getEmissions('Natural Gas Commercial', 2005);
      const waste2005 = getEmissions('Waste', 2005);
      const offroad2005 = getEmissions('Offroad', 2005);
      total2005 = transportation2005 + electricity2005 + naturalGas2005 + waste2005 + offroad2005;
    }

    // Calculate totals by source type for latest year
    const transportation = getEmissions('Light Duty Vehicles', latestYear) + getEmissions('Heavy Duty Vehicles', latestYear);
    const electricity = getEmissions('Electricity Residential', latestYear) + getEmissions('Electricity Commercial', latestYear);
    const naturalGas = getEmissions('Natural Gas Residential', latestYear) + getEmissions('Natural Gas Commercial', latestYear);
    const waste = getEmissions('Waste', latestYear);
    const offroad = getEmissions('Offroad', latestYear);
    const totalLatest = transportation + electricity + naturalGas + waste + offroad;

    // Calculate totals for 2020 if available
    let total2020 = 0;
    let transportation2020 = 0;
    let electricity2020 = 0;
    let naturalGas2020 = 0;
    if (year2020) {
      transportation2020 = getEmissions('Light Duty Vehicles', 2020) + getEmissions('Heavy Duty Vehicles', 2020);
      electricity2020 = getEmissions('Electricity Residential', 2020) + getEmissions('Electricity Commercial', 2020);
      naturalGas2020 = getEmissions('Natural Gas Residential', 2020) + getEmissions('Natural Gas Commercial', 2020);
      const waste2020 = getEmissions('Waste', 2020);
      const offroad2020 = getEmissions('Offroad', 2020);
      total2020 = transportation2020 + electricity2020 + naturalGas2020 + waste2020 + offroad2020;
    }

    // Linear extrapolation from 2005 to 2045
    let total2045 = 0;
    let percentChange2005to2045 = 0;
    let percentChange2020to2045 = 0;
    if (year2005 && total2005 > 0 && latestYear > 2005) {
      // Calculate annual change rate based on trend from 2005 to latest year
      const yearsElapsed = latestYear - 2005;
      const totalChange = totalLatest - total2005;
      const annualChangeRate = yearsElapsed > 0 ? totalChange / yearsElapsed : 0;
      
      // Project to 2045: total2045 = total2005 + annualChangeRate * (2045 - 2005)
      const yearsTo2045 = 2045 - 2005;
      total2045 = Math.max(0, total2005 + (annualChangeRate * yearsTo2045));
      
      // Calculate percentage changes
      percentChange2005to2045 = ((total2045 - total2005) / total2005) * 100;
      
      if (total2020 > 0) {
        percentChange2020to2045 = ((total2045 - total2020) / total2020) * 100;
      }
    }

    // Calculate percentage changes from 2005 (baseline) and 2020
    const percentChangeFrom2005 = total2005 > 0 ? ((totalLatest - total2005) / total2005) * 100 : 0;
    const percentChangeFrom2020 = total2020 > 0 ? ((totalLatest - total2020) / total2020) * 100 : 0;
    const percentChangeTransportation = transportation2020 > 0 ? ((transportation - transportation2020) / transportation2020) * 100 : 0;
    const percentChangeElectricity = electricity2020 > 0 ? ((electricity - electricity2020) / electricity2020) * 100 : 0;
    const percentChangeNaturalGas = naturalGas2020 > 0 ? ((naturalGas - naturalGas2020) / naturalGas2020) * 100 : 0;

    // Find largest source
    const sources = [
      { name: 'Transportation', value: transportation, percent: (transportation / totalLatest) * 100 },
      { name: 'Electricity', value: electricity, percent: (electricity / totalLatest) * 100 },
      { name: 'Natural Gas', value: naturalGas, percent: (naturalGas / totalLatest) * 100 },
      { name: 'Waste', value: waste, percent: (waste / totalLatest) * 100 },
      { name: 'Offroad', value: offroad, percent: (offroad / totalLatest) * 100 },
    ].filter(s => s.value > 0).sort((a, b) => b.value - a.value);

    return {
      latestYear,
      totalLatest,
      total2005,
      total2020,
      total2045,
      percentChangeFrom2005,
      percentChangeFrom2020,
      percentChange2005to2045,
      percentChange2020to2045,
      transportation,
      electricity,
      naturalGas,
      waste,
      offroad,
      percentChangeTransportation,
      percentChangeElectricity,
      percentChangeNaturalGas,
      largestSource: sources[0] || null,
      secondLargestSource: sources[1] || null,
    };
  }, [ghgData]);

  if (loading) {
    return (
      <ChartLayout
        title="Sector-Based GHG Inventory"
        description="Greenhouse gas emissions inventory organized by economic sectors and activities. These results are not intended to align directly with official community-wide inventories calculated. Consult your city or county GHG Inventory and Climate Action Plan for complementary approaches."
        chartType="line"
        keyMetrics={[]}
        chartContent={<div style={{ textAlign: 'center', padding: '40px' }}>Loading sector-based GHG data...</div>}
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Sector-based inventories help identify which economic sectors contribute most to emissions and where interventions can be most effective."
          >
            <div className="methodology-details-simple">
              <h5>Economic Impact</h5>
              <p>Understanding sector contributions helps prioritize climate action in areas with highest emissions impact.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Targeting</h5>
              <p>Sector-based data enables targeted policies for specific industries and activities.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Sector-Based GHG Inventory Methodology"
            description="This inventory follows standard protocols for tracking emissions by economic sector and activity type."
            dataSources={[
              'California Air Resources Board (CARB)',
              'Local utility data',
              'Transportation surveys',
              'Building energy data'
            ]}
            methodology={[
              '<strong>Data Collection:</strong> Gather emissions data from major sectors including transportation, energy, buildings, and waste',
              '<strong>Activity Data:</strong> Collect activity data such as vehicle miles traveled, energy consumption, and waste generation',
              '<strong>Emission Factors:</strong> Apply appropriate emission factors to convert activity data to CO₂ equivalent emissions',
              '<strong>Aggregation:</strong> Sum emissions by sector and year to create comprehensive inventory'
            ]}
          />
        }
        overviewContent={
          <OverviewContent
            title="Sector-Based GHG Inventory Overview"
            description="This inventory tracks greenhouse gas emissions organized by economic sectors and activities."
          >
            <p>The sector-based approach categorizes emissions by economic activity, making it easier to identify the largest sources and develop targeted reduction strategies.</p>
            <p>Key sectors typically include transportation, energy, buildings, waste, and other activities specific to the community.</p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Sector-based inventories provide the foundation for developing comprehensive climate action plans."
          >
            <div className="methodology-details-simple">
              <h5>Policy Development</h5>
              <p>Sector-based data helps policymakers prioritize actions and allocate resources effectively.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Progress Tracking</h5>
              <p>Regular inventories enable communities to track progress toward emission reduction goals.</p>
            </div>
          </RelevanceContent>
        }
        activeView={activeView}
        onViewChange={setActiveView}
        showRelevanceEquity={true}
      />
    );
  }

  if (error) {
    return (
      <ChartLayout
        title="Sector-Based GHG Inventory"
        description="Greenhouse gas emissions inventory organized by economic sectors and activities. These results are not intended to align directly with official community-wide inventories calculated. Consult your city or county GHG Inventory and Climate Action Plan for complementary approaches."
        chartType="line"
        keyMetrics={[]}
        chartContent={<div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>Error loading sector-based GHG data: {error}</div>}
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Sector-based inventories help identify which economic sectors contribute most to emissions and where interventions can be most effective."
          >
            <div className="methodology-details-simple">
              <h5>Economic Impact</h5>
              <p>Understanding sector contributions helps prioritize climate action in areas with highest emissions impact.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Targeting</h5>
              <p>Sector-based data enables targeted policies for specific industries and activities.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Sector-Based GHG Inventory Methodology"
            description="This inventory follows standard protocols for tracking emissions by economic sector and activity type."
            dataSources={[
              'California Air Resources Board (CARB)',
              'Local utility data',
              'Transportation surveys',
              'Building energy data'
            ]}
            methodology={[
              '<strong>Data Collection:</strong> Gather emissions data from major sectors including transportation, energy, buildings, and waste',
              '<strong>Activity Data:</strong> Collect activity data such as vehicle miles traveled, energy consumption, and waste generation',
              '<strong>Emission Factors:</strong> Apply appropriate emission factors to convert activity data to CO₂ equivalent emissions',
              '<strong>Aggregation:</strong> Sum emissions by sector and year to create comprehensive inventory'
            ]}
          />
        }
        overviewContent={
          <OverviewContent
            title="Sector-Based GHG Inventory Overview"
            description="This inventory tracks greenhouse gas emissions organized by economic sectors and activities."
          >
            <p>The sector-based approach categorizes emissions by economic activity, making it easier to identify the largest sources and develop targeted reduction strategies.</p>
            <p>Key sectors typically include transportation, energy, buildings, waste, and other activities specific to the community.</p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Sector-based inventories provide the foundation for developing comprehensive climate action plans."
          >
            <div className="methodology-details-simple">
              <h5>Policy Development</h5>
              <p>Sector-based data helps policymakers prioritize actions and allocate resources effectively.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Progress Tracking</h5>
              <p>Regular inventories enable communities to track progress toward emission reduction goals.</p>
            </div>
          </RelevanceContent>
        }
        activeView={activeView}
        onViewChange={setActiveView}
        showRelevanceEquity={true}
      />
    );
  }

  const keyMetrics = (
    <KeyMetrics>
      <KeyMetric 
        value={metrics?.totalLatest ? `${metrics.totalLatest.toLocaleString(undefined, {maximumFractionDigits: 0})} MT CO₂e` : '—'}
        label={`Total Emissions (${metrics?.latestYear || '2023'})`}
        color="#1e3a8a"
      />
      {metrics?.largestSource && (
        <KeyMetric 
          value={`${metrics.largestSource.percent.toFixed(1)}%`}
          label={`${metrics.largestSource.name} (largest source)`}
          color="#3b82f6"
        />
      )}
      {metrics?.percentChangeFrom2005 !== undefined && (
        <KeyMetric 
          value={metrics.percentChangeFrom2005 !== 0 ? `${metrics.percentChangeFrom2005 > 0 ? '+' : ''}${metrics.percentChangeFrom2005.toFixed(1)}%` : '0%'}
          label="Change from 2005"
          color={metrics.percentChangeFrom2005 > 0 ? "#ef4444" : "#10b981"}
        />
      )}
    </KeyMetrics>
  );

  return (
    <ChartLayout
      title="Sector-Based GHG Inventory"
      description="Greenhouse gas emissions inventory organized by economic sectors and activities. These results calculated by the CoolClimate Network at UC Berkeley are not intended to align directly with official community-wide inventories calculated using other methods. Consult your city or county for complimentary approaches."
      chartType="line"
      keyMetrics={keyMetrics}
      chartContent={
        <div>
          {(isElectricityDataUnderReview(selectedGeofips) || isElectricityDataUnderReviewByName(location)) && (
            <DataReviewBanner locationName={getLocationNameUnderReview(selectedGeofips) || getLocationNameUnderReview(getGeofipsFromLocationName(location)) || location} />
          )}
          <SectorBasedGHGInventory location={location} chartType={chartType} setChartType={setChartType} ghgData={ghgData} />
        </div>
      }
      chartSelector={{
        options: [
          { value: 'stacked-area', label: 'Stacked Area' },
          { value: 'line', label: 'Line Chart' },
          { value: 'pie', label: 'Pie Chart (2023)' }
        ],
        value: chartType,
        onChange: (value) => setChartType(value as ChartType)
      }}
      equityContent={
        <EquityContent
          title="Equity Considerations"
          description="GHG inventories must consider equity implications across different communities and demographic groups."
        >
          <div className="space-y-3">
            <p>
              <strong>Disproportionate Impacts:</strong> Some communities may face higher exposure to emissions sources and greater vulnerability to climate impacts.
            </p>
            <p>
              <strong>Access to Solutions:</strong> Climate solutions must be accessible to all income levels to ensure equitable participation in emissions reductions.
            </p>
            <p>
              <strong>Community Benefits:</strong> Climate investments should prioritize communities that have historically borne disproportionate environmental burdens.
            </p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Comprehensive GHG Inventory Methodology"
          description="Sector-based GHG inventory built from activity data and emission factors. Transportation (LDV/HDV), buildings (electricity and natural gas), waste, and offroad are calculated using the sources below."
          dataSources={[
            'VMT: Replica origin-destination travel demand model (2023 observed), CalTrans Average Annual Daily Traffic (AADT) + CARB Fleet Database (2015-2022 backcasting), EMFAC2021 model (2005-2014 backcasting)',
            'LDV emission factors: EMFAC2021 model (California Air Resources Board) for ghg_per_mile factors and backcasting ratios',
            'HDV emissions: EMFAC2021 model / CARB Fleet Database (2015–2023)',
            'Electricity consumption: PG&E (Pacific Gas & Electric), CEC (California Energy Commission), SCE (Southern California Edison), SDGE (San Diego Gas & Electric)',
            'Natural gas consumption: PG&E (Pacific Gas & Electric), CEC (California Energy Commission), SCE (Southern California Edison), SDGE (San Diego Gas & Electric)',
            'Electricity carbon intensity: eGRID utilities database (EPA) and CCA Power Content Labels',
            'Waste tonnage: CalRecycle (by location)',
            'Landfill emission factors: EPA Greenhouse Gas Reporting Program (GHGRP)',
            'Offroad emissions: CARB OFFROAD2021 Database',
            'Population: US Census Bureau'
          ]}
          methodology={[
            'Light-duty vehicles (LDV): VMT data sources: (1) 2023: Replica origin-destination travel demand model (observed data for PRIVATE_AUTO and ON_DEMAND_AUTO modes). (2) 2015-2022: Iterative AADT backcasting using CalTrans Average Annual Daily Traffic counts combined with CARB Fleet Database vehicle ownership data. Method: VMT_year = VMT_previous_year × AADT_scaling_factor, starting from 2023 backward. CalTrans monitors filtered for complete data 2015-2022. If 2022 VMT missing/zero, backcasted from 2023 using AADT. (3) 2005-2014: EMFAC2021 backcasting using county-level VMT change factors (class=LDV). Method: VMT_year = VMT_2015 × EMFAC_backcasting_ratio. This approach captures local changes in inbound, outbound and local traffic more accurately than purely modeled approaches. LDV emissions calculation: VMT × EMFAC2021 county ghg_per_mile factor (class=LDV). Uses fallback hierarchy (county year-specific > California year-specific > county average > California average > 0.0008 tons CO2/mile) with logging for debugging.',
            'Heavy-duty vehicles (HDV): Sum EMFAC2021 / CARB Fleet Database total_ghg for class=HDV by year (2015–2023). Backcast 2005–2014 using EMFAC2021 backcasting_ratio (class=HDV, county of the location).',
            'Electricity: Residential consumption data from PG&E, CEC, SCE, SDGE (by location geofips, no fallback/summing). Commercial consumption data from PG&E, CEC, SCE, SDGE. Provenance uses measurement column (observed/interpolated/extrapolated). Linear interpolation used for missing values between observed data points. Linear extrapolation used for forecasting and backcasting when data not available. Emissions = kWh × carbon_intensity (gCO₂/kWh ÷ 1,000). Carbon intensity priority: CCA Power Content Labels > eGRID utilities database; fallback 200 gCO₂/kWh.',
            'Natural gas: Residential and commercial consumption data from PG&E, CEC, SCE, SDGE. Linear interpolation used for missing values between observed data points. Linear extrapolation used for forecasting and backcasting when data not available. Emissions = therms × 0.005291 (MT CO₂e/therm).',
            'Waste: Waste tonnage data from CalRecycle by location. Waste streams traced to all landfills receiving waste from the location. County-level landfill emission factors calculated from EPA Greenhouse Gas Reporting Program (GHGRP). If county factor unavailable/invalid, California statewide average calculated. Waste emissions = waste_tons × emission_factor. Backcast to 2005 using linear extrapolation (shown as dotted line in charts).',
            'Offroad: Emissions data from CARB OFFROAD2021 Database for exact location geofips only; if none, set to zero (no county fallback).',
            'All emissions reported in metric tons CO₂e (MT CO₂e). Colors are solid and grouped by source family (transportation, natural gas, electricity, waste, offroad).'
          ]}
        />
      }
      overviewContent={
        <OverviewContent
          title="GHG Inventory Overview"
          description="Comprehensive greenhouse gas emissions tracking by economic sector."
        >
          {metrics ? (
            <>
              <p>
                {location} has total greenhouse gas emissions of {metrics.totalLatest.toLocaleString(undefined, {maximumFractionDigits: 0})} MT CO₂e in {metrics.latestYear}.
                {metrics.largestSource && (
                  <> The largest source is {metrics.largestSource.name} ({metrics.largestSource.percent.toFixed(1)}%), followed by {metrics.secondLargestSource ? metrics.secondLargestSource.name : 'other sources'} ({metrics.secondLargestSource ? metrics.secondLargestSource.percent.toFixed(1) + '%' : ''}).</>
                )}
              </p>
              
              {metrics.total2005 > 0 && (
                <p>
                  Emissions have {metrics.percentChangeFrom2005 > 0 ? 'increased' : 'decreased'} by {Math.abs(metrics.percentChangeFrom2005).toFixed(1)}% from 2005 ({metrics.total2005.toLocaleString(undefined, {maximumFractionDigits: 0})} MT CO₂e) to {metrics.latestYear} ({metrics.totalLatest.toLocaleString(undefined, {maximumFractionDigits: 0})} MT CO₂e).
                </p>
              )}
              
              {metrics.total2045 > 0 && metrics.total2020 > 0 && metrics.percentChange2020to2045 !== undefined && (
                <p>
                  Linear extrapolation from 2005 trends projects emissions of {metrics.total2045.toLocaleString(undefined, {maximumFractionDigits: 0})} MT CO₂e in 2045, a {Math.abs(metrics.percentChange2020to2045).toFixed(1)}% {metrics.percentChange2020to2045 > 0 ? 'increase' : 'decrease'} from 2020. California's goal of carbon neutrality by 2045 requires ~100% reduction from 2020, indicating additional policy interventions are needed to align with statewide targets.
                </p>
              )}
            </>
          ) : (
            <p>Loading GHG inventory data for {location}...</p>
          )}
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Relevance to Climate Action"
          description="GHG inventory data provides the foundation for climate action planning and emissions reduction strategies."
        >
          <div className="space-y-3">
            <p>
              <strong>Baseline Establishment:</strong> GHG inventories establish baseline emissions levels needed for setting reduction targets and tracking progress.
            </p>
            <p>
              <strong>Sector Prioritization:</strong> Understanding which sectors contribute most to emissions helps prioritize climate investments and policy interventions.
            </p>
            <p>
              <strong>Progress Tracking:</strong> Regular inventory updates enable monitoring of emission trends and evaluation of climate action effectiveness.
            </p>
          </div>
        </RelevanceContent>
      }
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
    />
  );
};